import React, { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarClock, Loader2 } from "lucide-react";

const TIMES = ["06:00 AM", "08:00 AM", "10:00 AM", "12:00 PM", "04:00 PM", "06:00 PM", "07:30 PM"];

export default function RescheduleDialog({ booking, availability, onDone }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [saving, setSaving] = useState(false);

  const slots = useMemo(() => {
    const av = availability || {};
    if (av && Object.keys(av).length > 0) return date ? (av[date] || []) : [];
    return TIMES;
  }, [availability, date]);

  const submit = async () => {
    if (!date || !time) { toast.error("Pick a new date and time"); return; }
    setSaving(true);
    try {
      await api.post(`/bookings/${booking.id}/reschedule`, { date, time });
      toast.success("Booking rescheduled — a confirmation email is on its way");
      setOpen(false); onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reschedule");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid={`reschedule-${booking.id}`} className="rounded-full border-accent/50 hover:bg-accent hover:text-accent-foreground"><CalendarClock className="h-4 w-4 mr-1" /> Reschedule</Button>
      </DialogTrigger>
      <DialogContent data-testid="reschedule-dialog">
        <DialogHeader><DialogTitle className="font-serif">Reschedule booking</DialogTitle>
        <DialogDescription>Pick a new date and open time slot for this booking.</DialogDescription></DialogHeader>
        <p className="text-sm text-muted-foreground">{booking.service_name} · {booking.purohit_name}</p>
        <div className="space-y-4 pt-1">
          <div>
            <Label className="mb-1.5 block">New date</Label>
            <Input type="date" data-testid="reschedule-date" value={date} min={new Date().toISOString().split("T")[0]} onChange={(e) => { setDate(e.target.value); setTime(""); }} className="rounded-xl max-w-xs" />
          </div>
          <div>
            <Label className="mb-1.5 block">New time slot</Label>
            {!date ? <p className="text-sm text-muted-foreground">Pick a date first.</p>
              : slots.length === 0 ? <p className="text-sm text-amber-600" data-testid="reschedule-no-slots">No open slots on this date. Choose another.</p>
              : <div className="flex flex-wrap gap-2">{slots.map((t) => (
                  <button key={t} onClick={() => setTime(t)} data-testid={`reschedule-time-${t.replace(/[:\s]/g, "")}`} className={`px-3.5 py-1.5 rounded-full text-sm border ${time === t ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-accent"}`}>{t}</button>
                ))}</div>}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} data-testid="reschedule-confirm" className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground">
            {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</> : "Confirm new time"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
